import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { Direction } from '@shared/types/entity';
import { getClassDisplayName } from '@shared/types/character';
import { getFactionColor } from '@shared/data/factions';
import {
  PARTY_NAME_COLOR,
  PARTY_XP_SHARE_MAX,
  type PartyView,
  type PartyMemberView,
  type PartyDistributionPolicy,
  type PartyQuestPolicy,
} from '@shared/data/party';
import { getHeadSpriteKey, getHeadFrameForDirection } from '../../sprites/HeadSpriteManager';
import { getBodySpriteKey, getBodyIdleFrame } from '../../sprites/BodySpriteManager';

/** Acciones que el panel dispara hacia el servidor (las cablea PartyController). */
export interface PartyPanelCallbacks {
  onKick: (sessionId: string) => void;
  onTransferLeader: (sessionId: string) => void;
  onReorder: (order: string[]) => void;
  onSetPolicy: (p: {
    goldPolicy?: PartyDistributionPolicy;
    lootPolicy?: PartyDistributionPolicy;
    questPolicy?: PartyQuestPolicy;
  }) => void;
  onSetXpShares: (shares: Record<string, number> | null) => void;
  onLeave: () => void;
  onDisband: () => void;
  onClose?: () => void;
}

// Paleta (misma familia dorada que el resto de la UI).
const C_PANEL = 0x16130b;
const C_HEADER = 0x261d0e;
const C_ACCENT = 0xc79a3e;
const C_ROW = 0x1d1810;
const C_ROW_SEL = 0x3a2e14;
const C_HP_BG = 0x3a1414;
const C_HP = 0x46c46a;
const C_BTN = 0x2a2210;
const T_BODY = '#e8dcb0';
const T_DIM = '#9a8a5a';

const WIDTH = 320;
const HEADER_H = 34;
const ROW_H = 52;
const AVATAR = 40;
const PAD = 10;

function font<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

const GOLD_LOOT_LABELS: Record<PartyDistributionPolicy, string> = {
  even: 'Equitativo',
  random: 'Al azar',
  leader: 'Al líder',
  round_robin: 'Rotativo',
};
const QUEST_LABELS: Record<PartyQuestPolicy, string> = {
  order: 'Por orden',
  round_robin: 'Rotativo',
  leader: 'Al líder',
};
const GOLD_LOOT_CYCLE: PartyDistributionPolicy[] = ['round_robin', 'even', 'random', 'leader'];
const QUEST_CYCLE: PartyQuestPolicy[] = ['round_robin', 'order', 'leader'];

/**
 * PartyPanel — ventana de gestión del party (roster con cara + nombre + clase +
 * vida, corona del líder, reordenar, expulsar, pasar liderazgo, políticas de
 * reparto y editor de % de XP). Se reconstruye en cada PARTY_SYNC.
 * Ver docs/PARTY_SYSTEM.md.
 */
export class PartyPanel {
  private readonly scene: Phaser.Scene;
  private readonly cb: PartyPanelCallbacks;
  private root: Phaser.GameObjects.Container;
  private x: number;
  private y: number;
  private visible = false;

  private localSessionId = '';
  private view: PartyView | null = null;
  private selectedSid: string | null = null;
  /** Borrador de % de XP en edición (sessionId→%). null = no editando. */
  private xpDraft: Record<string, number> | null = null;

  // Header (persistente) + body (se reconstruye).
  private header!: Phaser.GameObjects.Container;
  private body: Phaser.GameObjects.Container;

  constructor(scene: Phaser.Scene, cb: PartyPanelCallbacks, initialX: number, initialY: number) {
    this.scene = scene;
    this.cb = cb;
    this.x = initialX;
    this.y = initialY;
    this.root = scene.add.container(this.x, this.y).setDepth(1040).setScrollFactor(0);
    this.body = scene.add.container(0, HEADER_H);
    this.root.add(this.body);
    this.buildHeader();
    this.root.setVisible(false);
  }

  // === API ==================================================================

  setLocalSessionId(id: string): void {
    this.localSessionId = id;
  }

  isVisible(): boolean {
    return this.visible;
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  show(): void {
    this.visible = true;
    this.root.setVisible(true);
    this.rebuild();
  }

  hide(): void {
    this.visible = false;
    this.root.setVisible(false);
  }

  /**
   * Recibe un PARTY_SYNC. Si party = null NO se cierra: muestra el estado "sin
   * grupo" (así la tecla abre la ventana aunque no estés en un party).
   */
  update(view: PartyView | null): void {
    this.view = view;
    if (!view) {
      this.selectedSid = null;
      this.xpDraft = null;
    } else if (this.selectedSid && !view.members.some((m) => m.sessionId === this.selectedSid)) {
      // Si el seleccionado ya no está, deseleccionar.
      this.selectedSid = null;
    }
    if (this.visible) this.rebuild();
  }

  // === BUILD ================================================================

  private isLeader(): boolean {
    return !!this.view && this.view.leaderSessionId === this.localSessionId;
  }

  private buildHeader(): void {
    this.header = this.scene.add.container(0, 0);
    const bg = this.scene.add.rectangle(0, 0, WIDTH, HEADER_H, C_HEADER).setOrigin(0, 0);
    bg.setStrokeStyle(1, C_ACCENT, 0.6);
    bg.setInteractive({ useHandCursor: true });
    this.enableDrag(bg);
    const title = this.scene.add
      .text(PAD, HEADER_H / 2, 'GRUPO', font({ fontSize: '16px', color: T_BODY }))
      .setOrigin(0, 0.5);
    const close = this.scene.add
      .text(WIDTH - PAD, HEADER_H / 2, '✕', font({ fontSize: '16px', color: T_DIM }))
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    close.on('pointerdown', () => {
      this.hide();
      this.cb.onClose?.();
    });
    this.header.add([bg, title, close]);
    this.root.add(this.header);
  }

  private enableDrag(handle: Phaser.GameObjects.Rectangle): void {
    let dragging = false;
    let ox = 0;
    let oy = 0;
    handle.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragging = true;
      ox = p.x - this.x;
      oy = p.y - this.y;
    });
    this.scene.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!dragging) return;
      this.x = p.x - ox;
      this.y = p.y - oy;
      this.root.setPosition(this.x, this.y);
    });
    this.scene.input.on('pointerup', () => {
      dragging = false;
    });
  }

  private rebuild(): void {
    this.body.removeAll(true);
    if (!this.view) {
      // Estado "sin grupo": la ventana abre igual y explica cómo formar uno.
      const bodyH = 92;
      const bg = this.scene.add.rectangle(0, -HEADER_H, WIDTH, HEADER_H + bodyH, C_PANEL).setOrigin(0, 0);
      bg.setStrokeStyle(1, C_ACCENT, 0.5);
      this.body.add(bg);
      const msg = this.scene.add
        .text(PAD, 18, 'No estás en ningún grupo.', font({ fontSize: '13px', color: T_BODY }))
        .setOrigin(0, 0);
      const hint = this.scene.add
        .text(
          PAD,
          42,
          'Invitá con  /invitar <nombre>\no buscá al Heraldo de Grupos en la ciudad.',
          font({ fontSize: '11px', color: T_DIM })
        )
        .setOrigin(0, 0);
      this.body.add([msg, hint]);
      return;
    }
    const view = this.view;
    const leader = this.isLeader();

    let y = 0;
    const members = view.members;

    // Filas de miembros.
    members.forEach((m, i) => {
      this.buildMemberRow(m, y, i, members.length);
      y += ROW_H;
    });

    // Panel de fondo detrás de las filas (se dibuja primero visualmente: lo mandamos al fondo).
    const bodyH = y + this.footerHeight();
    const panelBg = this.scene.add.rectangle(0, -HEADER_H, WIDTH, HEADER_H + bodyH, C_PANEL).setOrigin(0, 0);
    panelBg.setStrokeStyle(1, C_ACCENT, 0.5);
    this.body.add(panelBg);
    this.body.sendToBack(panelBg);

    // Footer: acciones + políticas + XP.
    this.buildFooter(y, leader);
  }

  private footerHeight(): number {
    // acciones (1 fila) + políticas (3 filas) + XP (variable).
    const base = 34 + 3 * 26 + 12;
    if (this.xpDraft && this.view) return base + 26 + this.view.members.length * 24 + 34;
    return base + 24;
  }

  private buildMemberRow(m: PartyMemberView, y: number, index: number, total: number): void {
    const isSel = this.selectedSid === m.sessionId;
    const row = this.scene.add.rectangle(0, y, WIDTH, ROW_H - 2, isSel ? C_ROW_SEL : C_ROW).setOrigin(0, 0);
    row.setInteractive({ useHandCursor: true });
    row.on('pointerdown', () => {
      this.selectedSid = this.selectedSid === m.sessionId ? null : m.sessionId;
      this.rebuild();
    });
    this.body.add(row);

    // Avatar (cara a la izquierda).
    const face = this.createFace(m.headId, m.bodyId);
    const avatarBg = this.scene.add
      .rectangle(PAD, y + (ROW_H - 2) / 2, AVATAR + 4, AVATAR + 4, 0x000000, 0.35)
      .setOrigin(0, 0.5);
    this.body.add(avatarBg);
    if (face) {
      face.setPosition(PAD + (AVATAR + 4) / 2, y + (ROW_H - 2) / 2);
      this.body.add(face);
    }

    // Nombre + clase/nivel a la derecha.
    const nameColor = m.sessionId === this.localSessionId ? PARTY_NAME_COLOR : T_BODY;
    const crown = m.isLeader ? '★ ' : '';
    const name = this.scene.add
      .text(PAD + AVATAR + 12, y + 8, `${crown}${m.name}`, font({ fontSize: '14px', color: nameColor }))
      .setOrigin(0, 0);
    const sub = this.scene.add
      .text(
        PAD + AVATAR + 12,
        y + 24,
        `Nv ${m.level} · ${getClassDisplayName(m.classId)}`,
        font({ fontSize: '11px', color: T_DIM })
      )
      .setOrigin(0, 0);
    this.body.add([name, sub]);

    // Barra de vida.
    const barX = PAD + AVATAR + 12;
    const barW = WIDTH - barX - PAD - (this.selectedSid === m.sessionId ? 0 : 0);
    const barY = y + 40;
    const hpBg = this.scene.add.rectangle(barX, barY, barW, 6, C_HP_BG).setOrigin(0, 0.5);
    const frac = m.maxHp > 0 ? Math.max(0, Math.min(1, m.currentHp / m.maxHp)) : 0;
    const hp = this.scene.add.rectangle(barX, barY, Math.max(1, barW * frac), 6, C_HP).setOrigin(0, 0.5);
    this.body.add([hpBg, hp]);

    // Facción (puntito de color).
    if (m.faction) {
      const dot = this.scene.add
        .text(WIDTH - PAD, y + 8, '●', font({ fontSize: '12px', color: getFactionColor(m.faction as never) }))
        .setOrigin(1, 0);
      this.body.add(dot);
    }

    // Reordenar (flechas) para el líder.
    if (this.isLeader()) {
      if (index > 0) this.addMiniBtn(WIDTH - PAD - 16, y + 26, '▲', () => this.move(index, index - 1));
      if (index < total - 1) this.addMiniBtn(WIDTH - PAD, y + 26, '▼', () => this.move(index, index + 1));
    }
  }

  private addMiniBtn(x: number, y: number, label: string, onClick: () => void): void {
    const t = this.scene.add
      .text(x, y, label, font({ fontSize: '12px', color: T_DIM }))
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    t.on('pointerdown', onClick);
    this.body.add(t);
  }

  private move(from: number, to: number): void {
    if (!this.view) return;
    const order = this.view.members.map((m) => m.sessionId);
    const [moved] = order.splice(from, 1);
    order.splice(to, 0, moved);
    this.cb.onReorder(order);
  }

  private buildFooter(y: number, leader: boolean): void {
    let fy = y + 6;

    // Acciones del miembro seleccionado (solo líder, y no sobre sí mismo).
    if (leader && this.selectedSid && this.selectedSid !== this.localSessionId) {
      this.addButton(PAD, fy, 130, 'Pasar liderazgo', () => {
        if (this.selectedSid) this.cb.onTransferLeader(this.selectedSid);
      });
      this.addButton(WIDTH - PAD - 90, fy, 90, 'Expulsar', () => {
        if (this.selectedSid) this.cb.onKick(this.selectedSid);
      });
      fy += 34;
    }

    // Políticas (solo líder puede cambiarlas; todos ven el valor).
    if (this.view) {
      this.addPolicyRow(fy, 'Oro', GOLD_LOOT_LABELS[this.view.goldPolicy], leader, () =>
        this.cyclePolicy('gold')
      );
      fy += 26;
      this.addPolicyRow(fy, 'Items', GOLD_LOOT_LABELS[this.view.lootPolicy], leader, () =>
        this.cyclePolicy('loot')
      );
      fy += 26;
      this.addPolicyRow(fy, 'Quest', QUEST_LABELS[this.view.questPolicy], leader, () =>
        this.cyclePolicy('quest')
      );
      fy += 30;
    }

    // Editor de reparto de XP (solo si el local porta la habilidad).
    if (this.view?.canEditXpShares) {
      this.buildXpEditor(fy);
      fy += 26 + (this.xpDraft ? this.view.members.length * 24 + 34 : 0);
    }

    // Salir / disolver.
    if (leader) {
      this.addButton(PAD, fy, WIDTH - PAD * 2, 'Disolver grupo', () => this.cb.onDisband());
    } else {
      this.addButton(PAD, fy, WIDTH - PAD * 2, 'Salir del grupo', () => this.cb.onLeave());
    }
  }

  private addPolicyRow(y: number, label: string, value: string, canEdit: boolean, onClick: () => void): void {
    const l = this.scene.add.text(PAD, y, label, font({ fontSize: '12px', color: T_DIM })).setOrigin(0, 0);
    const v = this.scene.add
      .text(WIDTH - PAD, y, value + (canEdit ? '  ⟳' : ''), font({ fontSize: '12px', color: canEdit ? '#ffe6a0' : T_BODY }))
      .setOrigin(1, 0);
    if (canEdit) {
      v.setInteractive({ useHandCursor: true });
      v.on('pointerdown', onClick);
    }
    this.body.add([l, v]);
  }

  private cyclePolicy(which: 'gold' | 'loot' | 'quest'): void {
    if (!this.view) return;
    if (which === 'quest') {
      const cur = this.view.questPolicy;
      const next = QUEST_CYCLE[(QUEST_CYCLE.indexOf(cur) + 1) % QUEST_CYCLE.length];
      this.cb.onSetPolicy({ questPolicy: next });
    } else {
      const cur = which === 'gold' ? this.view.goldPolicy : this.view.lootPolicy;
      const next = GOLD_LOOT_CYCLE[(GOLD_LOOT_CYCLE.indexOf(cur) + 1) % GOLD_LOOT_CYCLE.length];
      this.cb.onSetPolicy(which === 'gold' ? { goldPolicy: next } : { lootPolicy: next });
    }
  }

  private buildXpEditor(y: number): void {
    if (!this.view) return;
    const view = this.view;
    if (!this.xpDraft) {
      // Botón para abrir el editor.
      const open = this.scene.add
        .text(PAD, y, '⚖ Repartir experiencia…', font({ fontSize: '12px', color: '#ffe6a0' }))
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      open.on('pointerdown', () => {
        // Semilla: reparto actual o parejo.
        const seed: Record<string, number> = {};
        const n = view.members.length;
        const evenPct = Math.floor(100 / n);
        view.members.forEach((m, i) => {
          seed[m.sessionId] = view.xpShares?.[m.sessionId] ?? (i === 0 ? 100 - evenPct * (n - 1) : evenPct);
        });
        this.xpDraft = seed;
        this.rebuild();
      });
      this.body.add(open);
      return;
    }

    const draft = this.xpDraft;
    const title = this.scene.add
      .text(PAD, y, 'Reparto de XP (máx 75%, total 100%)', font({ fontSize: '11px', color: T_DIM }))
      .setOrigin(0, 0);
    this.body.add(title);
    let ry = y + 18;
    let sum = 0;
    for (const m of view.members) {
      const pct = draft[m.sessionId] ?? 0;
      sum += pct;
      const nm = this.scene.add
        .text(PAD, ry, m.name, font({ fontSize: '11px', color: T_BODY }))
        .setOrigin(0, 0);
      this.addMiniBtn(WIDTH - PAD - 60, ry + 6, '−', () => this.bumpXp(m.sessionId, -5));
      const val = this.scene.add
        .text(WIDTH - PAD - 30, ry, `${pct}%`, font({ fontSize: '11px', color: '#ffe6a0' }))
        .setOrigin(0.5, 0);
      this.addMiniBtn(WIDTH - PAD, ry + 6, '+', () => this.bumpXp(m.sessionId, 5));
      this.body.add([nm, val]);
      ry += 24;
    }
    const sumOk = sum === 100;
    const sumTxt = this.scene.add
      .text(PAD, ry, `Total: ${sum}%`, font({ fontSize: '11px', color: sumOk ? '#8fe0a0' : '#e06a6a' }))
      .setOrigin(0, 0);
    this.body.add(sumTxt);
    this.addButton(WIDTH - PAD - 150, ry - 4, 70, 'Parejo', () => {
      this.xpDraft = null;
      this.cb.onSetXpShares(null);
    });
    this.addButton(WIDTH - PAD - 74, ry - 4, 74, 'Aplicar', () => {
      if (this.xpDraft) this.cb.onSetXpShares({ ...this.xpDraft });
    });
  }

  private bumpXp(sid: string, delta: number): void {
    if (!this.xpDraft) return;
    const cur = this.xpDraft[sid] ?? 0;
    const next = Math.max(0, Math.min(PARTY_XP_SHARE_MAX, cur + delta));
    this.xpDraft[sid] = next;
    this.rebuild();
  }

  private addButton(x: number, y: number, w: number, label: string, onClick: () => void): void {
    const bg = this.scene.add.rectangle(x, y, w, 24, C_BTN).setOrigin(0, 0);
    bg.setStrokeStyle(1, C_ACCENT, 0.5);
    bg.setInteractive({ useHandCursor: true });
    const t = this.scene.add
      .text(x + w / 2, y + 12, label, font({ fontSize: '12px', color: T_BODY }))
      .setOrigin(0.5, 0.5);
    bg.on('pointerdown', onClick);
    this.body.add([bg, t]);
  }

  /** Crea el sprite de la cara (head; body como fallback), escalado a AVATAR. */
  private createFace(headId: number, bodyId: number): Phaser.GameObjects.Sprite | null {
    let key: string | null = null;
    let frame: number | string = 0;
    if (headId > 0 && this.scene.textures.exists(getHeadSpriteKey(headId))) {
      key = getHeadSpriteKey(headId);
      frame = getHeadFrameForDirection(Direction.DOWN);
    } else if (bodyId > 0 && this.scene.textures.exists(getBodySpriteKey(bodyId))) {
      key = getBodySpriteKey(bodyId);
      frame = getBodyIdleFrame(Direction.DOWN, bodyId);
    }
    if (!key) return null;
    const spr = this.scene.add.sprite(0, 0, key, frame);
    const scale = AVATAR / Math.max(1, Math.max(spr.width, spr.height));
    spr.setScale(scale);
    return spr;
  }

  destroy(): void {
    this.root.destroy(true);
  }
}
